export interface HoneypotProps {
  /** Prefixed onto the field's id so two forms on one page cannot collide. */
  id: string
  /** The accessible label. A real person who reaches it needs to know to leave it alone. */
  label: string
  /** Called with the field's element so a submit handler can read it. */
  inputRef?: (element: HTMLInputElement | null) => void
}

/** The field's name, on every form. The API checks this one key. */
export const HONEYPOT_FIELD = 'kkf_contact_pref'

/**
 * A field no person should ever fill in.
 *
 * The cheapest anti-spam measure there is, and the only one that costs a visitor nothing:
 * no puzzle, no third party, no network request, no cookie. A scripted form-filler
 * populates every input it finds; a person does not see this one.
 *
 * ## It is hidden from sight, not from assistive technology
 *
 * `display: none` would hide it from a screen reader too — and then somebody using one
 * would be the **only** person who fills it in, and the only person silently refused. So
 * it is positioned off-screen, carries a real `<label>` telling anybody who reaches it to
 * leave it empty, and is `tabIndex={-1}` so it is not in the keyboard order.
 *
 * `autoComplete="off"` matters more than it looks: a password manager filling this field
 * would refuse a real person just as effectively as a bot.
 *
 * ## What it is not
 *
 * **It is not sufficient on its own.** It stops indiscriminate form-filling and nothing
 * targeted — anybody who looks at the markup once can skip it forever. It is the free half
 * of the answer; `Turnstile` is the half that costs something.
 *
 * The check belongs on the **server**. `HONEYPOT_FIELD` is exported so the API and the
 * front end name the same key, and a front-end-only check is one a script never sees.
 */
export function Honeypot({ id, label, inputRef }: HoneypotProps) {
  return (
    <div className="ul-honeypot">
      <label htmlFor={`${id}-${HONEYPOT_FIELD}`}>{label}</label>
      <input
        ref={inputRef}
        id={`${id}-${HONEYPOT_FIELD}`}
        name={HONEYPOT_FIELD}
        type="text"
        autoComplete="off"
        tabIndex={-1}
        defaultValue=""
      />
    </div>
  )
}
